/**
 * Alpine handler for the moderator appeal detail page.
 * Posts approve/deny decisions to /v1/appeals/{id}/decide via window.ApiClient.
 *
 * Wires up the data shape consumed by mod/appeals/detail.twig:
 *   - approve.{reasonCategory, logMsg}
 *   - deny.{denialOutcomeCategory, logMsg, subtype, cooldownDays}
 *
 * Confirmation goes through window.ConfirmModal so the action is acknowledged
 * before the request is sent. A 409 response is treated as "already decided"
 * and surfaces a friendly message rather than a generic error.
 */
(function () {
    'use strict';

    function postFormUrlEncoded(endpoint, params) {
        const body = new URLSearchParams();
        Object.keys(params).forEach((key) => {
            const value = params[key];
            if (value === null || value === undefined || value === '') {
                return;
            }
            body.append(key, String(value));
        });

        return window.ApiClient.request(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        });
    }

    function confirm(options) {
        return new Promise((resolve) => {
            if (!window.ConfirmModal || typeof window.ConfirmModal.show !== 'function') {
                resolve(window.confirm(options.message));
                return;
            }
            window.ConfirmModal.show({
                title: options.title,
                message: options.message,
                confirmText: options.confirmText,
                cancelText: 'Cancel',
                type: options.type || 'warning',
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
    }

    function modAppealDecide(config) {
        return {
            appealId: config.appealId,
            appealsListUrl: config.appealsListUrl || '/app/mod/appeals',
            error: '',

            approve: {
                reasonCategory: 'restore-mistake',
                logMsg: '',
            },
            deny: {
                denialOutcomeCategory: '',
                logMsg: '',
                subtype: '',
                cooldownDays: '',
            },

            async approveAppeal() {
                this.error = '';

                // Approve is single-click on purpose: restoring an account is
                // recoverable (the moderator can re-suspend), and the validation
                // gates above (log message present, valid restore reason in the
                // <select>) already establish intent.

                try {
                    const response = await postFormUrlEncoded(`/appeals/${this.appealId}/decide`, {
                        decision: 'approved',
                        reason_category: this.approve.reasonCategory,
                        log_msg: this.approve.logMsg,
                    });

                    if (response.ok) {
                        window.location.href = this.appealsListUrl;
                        return;
                    }
                    await this.handleErrorResponse(response);
                } catch (err) {
                    this.error = (err && err.message) ? err.message : 'Network error';
                }
            },

            async denyAppeal() {
                this.error = '';
                if (!this.deny.denialOutcomeCategory) {
                    this.error = 'Outcome category is required.';
                    return;
                }
                const cooldownInput = String(this.deny.cooldownDays).trim();
                const cooldown = cooldownInput === '' ? null : Number(cooldownInput);
                if (
                    cooldown !== null
                    && (!/^\d+$/.test(cooldownInput) || !Number.isSafeInteger(cooldown) || cooldown < 1 || cooldown > 365)
                ) {
                    this.error = 'Cooldown must be a whole number from 1-365 days.';
                    return;
                }

                // Long cooldowns (>= 90 days) push the user into a state that's
                // hard to recover from in a hurry, so escalate the modal to
                // "danger" and call out the cooldown length.
                const longCooldown = cooldown !== null && cooldown >= 90;
                const ok = await confirm({
                    title: 'Deny appeal',
                    message: longCooldown
                        ? `Deny this appeal with a ${cooldown}-day cooldown? The user cannot resubmit until that date.`
                        : 'Deny this appeal? The user will see the configured outcome on their status page.',
                    confirmText: 'Deny appeal',
                    type: longCooldown ? 'danger' : 'warning',
                });
                if (!ok) {
                    return;
                }

                try {
                    const response = await postFormUrlEncoded(`/appeals/${this.appealId}/decide`, {
                        decision: 'denied',
                        denial_outcome_category: this.deny.denialOutcomeCategory,
                        log_msg: this.deny.logMsg,
                        subtype: this.deny.subtype,
                        cooldown_days: cooldown,
                    });

                    if (response.ok) {
                        window.location.href = this.appealsListUrl;
                        return;
                    }
                    await this.handleErrorResponse(response);
                } catch (err) {
                    this.error = (err && err.message) ? err.message : 'Network error';
                }
            },

            async handleErrorResponse(response) {
                if (response.status === 409) {
                    this.error = 'This appeal has already been decided. Refresh to see the latest state.';
                    return;
                }
                let message = 'Request failed (' + response.status + ').';
                try {
                    const body = await response.json();
                    if (body && body.error && body.error.message) {
                        message = body.error.message;
                    }
                } catch (_e) {
                    // Body wasn't JSON; fall back to generic message.
                }
                this.error = message;
            },
        };
    }

    document.addEventListener('alpine:init', () => {
        if (window.Alpine) {
            window.Alpine.data('modAppealDecide', modAppealDecide);
        }
    });

    // Expose for non-Alpine callers (debug, tests).
    window.modAppealDecide = modAppealDecide;
})();
